/**
 * Editorial per-station content (content spec 2026-08-05): static
 * per-station JSON in the repo, read at runtime by the SPA and at
 * build time by the prerenderer.
 *
 *   public/station-content/en/<naptanId>.json    — master record
 *   public/station-content/<lng>/<naptanId>.json — locale OVERLAY
 *
 * The en record carries the data fields (address, exits, landmarks)
 * plus the English editorial description; a prerender locale's overlay
 * carries the localized prose fields only (description) and wins
 * per-field over the en base. Proper nouns stay English everywhere.
 */
/** One station exit in the Exits and transport section. */
export interface StationExit {
  /** Exit name, e.g. `Marylebone Road`. */
  readonly name: string
  /** What's near this exit, e.g. `Madame Tussauds · Regent's Park`. */
  readonly nearby?: string
}

export interface StationContent {
  /** Street address, e.g. `Marylebone Road, NW1 5LA`. */
  readonly address?: string
  /** 1–2 sentence editorial description shown under the H1. */
  readonly description?: string
  /** Exits with their nearby landmarks (Exits and transport section). */
  readonly exits?: readonly StationExit[]
  /**
   * Places this station is used to reach — a STATION-level fact, so it
   * exists for stations with no exits data (the great majority). Ordered
   * as a local would name them; empty where the station genuinely has
   * nothing notable nearby, which omits the About sentence rather than
   * inventing one.
   */
  readonly landmarks?: readonly string[]
}

async function fetchRecord(locale: string, naptanId: string): Promise<StationContent | null> {
  try {
    const res = await fetch(`/station-content/${locale}/${naptanId}.json`)
    if (!res.ok) return null
    return (await res.json()) as StationContent
  } catch {
    return null
  }
}

/**
 * Fail-safe reader (Safe-client rule 2): a missing file, bad JSON or
 * network error degrades to the en base (or `{}`) — the facts rows for
 * absent fields simply don't render. Never throws into the page.
 */
export async function fetchStationContent(
  naptanId: string,
  locale: string,
): Promise<StationContent> {
  const [base, overlay] = await Promise.all([
    fetchRecord('en', naptanId),
    locale === 'en' ? Promise.resolve(null) : fetchRecord(locale, naptanId),
  ])
  if (base === null && overlay === null) return {}
  return { ...(base ?? {}), ...(overlay ?? {}) }
}
